// The item drawer's small controls. Every text input renders at 16px (the root font is 13px), so iOS Safari does not
// zoom on focus. Text saves on blur: there is no Save button (DD-051 drawers have no Cancel, and the X closes).
import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { inputClassName } from "@/components/ui/FormField";
import { cn } from "@/utils/cn";

/** The shared input look at the drawer's size: 40px tall, 16px text. */
export const fieldClass = cn(inputClassName, "h-[40px] text-[16px] text-gray-900 placeholder:text-gray-400");
export const areaClass = cn(inputClassName, "h-auto min-h-[64px] py-2 text-[16px] leading-[22px] text-gray-900 placeholder:text-gray-400 resize-none");

/** A section of the drawer: a 13px heading, an optional aside, then its body. */
export function Section({ title, aside, children, className }: { title: string; aside?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <section className={className} aria-label={title}>
      <div className="mb-2 flex items-center gap-2">
        <h3 className="text-[13px] font-semibold text-gray-900">{title}</h3>
        {aside && <div className="ml-auto">{aside}</div>}
      </div>
      {children}
    </section>
  );
}

/** A label above its control, at 12px (FormField's text-xs renders under 10px on the 13px root). */
export function Field({ label, children, className }: { label: string; children: ReactNode; className?: string }) {
  return (
    <label className={cn("block", className)}>
      <span className="mb-1 block text-[12px] font-medium text-gray-600">{label}</span>
      {children}
    </label>
  );
}

interface BlurTextProps {
  value: string;
  onCommit: (next: string) => void;
  multiline?: boolean;
  /** One line of text that wraps as it grows (a long name): a textarea where Enter saves and new lines never land. */
  wrap?: boolean;
  /** An empty value reverts to the last saved one instead of saving (the name). */
  required?: boolean;
  placeholder?: string;
  ariaLabel?: string;
  className?: string;
  disabled?: boolean;
  rows?: number;
  autoFocus?: boolean;
  /** A list id for suggestions (a native datalist). */
  list?: string;
}

/**
 * A text input that keeps its own draft while focused and saves once, on blur, when the text changed. Enter in a
 * single line blurs it. An unmount (Escape, the X, ‹ ›) saves a draft the blur did not.
 */
export function BlurText({ value, onCommit, multiline = false, wrap = false, required = false, className, ...rest }: BlurTextProps) {
  const [draft, setDraft] = useState(value);
  const area = useRef<HTMLTextAreaElement>(null);
  // A textarea grows with its text up to about five lines (a description), then scrolls, so the first screen stays short.
  useLayoutEffect(() => {
    const el = area.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight + 2}px`;
  }, [draft]);
  const focused = useRef(false);
  const saved = useRef(value);
  const latest = useRef({ draft, onCommit, required, multiline });
  latest.current = { draft, onCommit, required, multiline };

  useEffect(() => {
    saved.current = value;
    if (!focused.current) setDraft(value);
  }, [value]);

  useEffect(() => () => {
    const { draft: text, onCommit: commit, required: needed, multiline: area } = latest.current;
    const next = area ? text : text.trim();
    if (next !== saved.current && !(needed && next === "")) commit(next);
  }, []);

  const commit = () => {
    focused.current = false;
    const next = multiline ? draft : draft.trim();
    if (required && next === "") {
      setDraft(saved.current);
      return;
    }
    if (next !== draft) setDraft(next);
    if (next === saved.current) return;
    saved.current = next;
    onCommit(next);
  };

  const shared = {
    value: draft,
    onFocus: () => {
      focused.current = true;
    },
    onBlur: commit,
    "aria-label": rest.ariaLabel,
    placeholder: rest.placeholder,
    disabled: rest.disabled,
    autoFocus: rest.autoFocus,
  };
  if (wrap) {
    return (
      <textarea
        {...shared}
        ref={area}
        rows={1}
        enterKeyHint="done"
        onChange={(e) => setDraft(e.target.value.replace(/\n/g, " "))}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            e.currentTarget.blur();
          }
        }}
        className={cn(areaClass, "min-h-0 overflow-hidden", className)}
      />
    );
  }
  return multiline ? (
    <textarea
      {...shared}
      ref={area}
      rows={rest.rows ?? 2}
      onChange={(e) => setDraft(e.target.value)}
      className={cn(areaClass, "max-h-[136px] overflow-y-auto", className)}
    />
  ) : (
    <input
      {...shared}
      type="text"
      list={rest.list}
      enterKeyHint="done"
      onChange={(e) => setDraft(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          e.currentTarget.blur();
        }
      }}
      className={cn(fieldClass, className)}
    />
  );
}

export interface SegmentOption<T extends string> {
  value: T;
  label: string;
  /** The radio's accessible name when its visible label is a sign ("—" reads "Nessuno"). */
  ariaLabel?: string;
  /** The chosen look, when one choice must stand out (Contiene). */
  activeClass?: string;
}

/** A segmented control: one choice of a few, as a radio group. `className` sets the track's layout. */
export function Segmented<T extends string>({
  options, value, onChange, ariaLabel, className, disabled = false,
}: {
  options: SegmentOption<T>[];
  value: T;
  onChange: (next: T) => void;
  ariaLabel: string;
  className?: string;
  disabled?: boolean;
}) {
  return (
    <div role="radiogroup" aria-label={ariaLabel} className={cn("inline-flex gap-[2px] rounded-lg bg-gray-100 p-[2px]", className)}>
      {options.map((option) => {
        const on = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={on}
            aria-label={option.ariaLabel}
            disabled={disabled}
            onClick={() => {
              if (!on) onChange(option.value);
            }}
            className={cn(
              "min-h-[32px] rounded-md px-2.5 text-[12px] leading-4 font-medium transition-colors whitespace-nowrap",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400",
              on ? (option.activeClass ?? "bg-white text-gray-900 shadow-sm") : "text-gray-500 hover:text-gray-800",
              disabled && "opacity-50",
            )}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

/** A toggle chip, for labels. */
export function Chip({ on, onClick, children, disabled = false }: { on: boolean; onClick: () => void; children: ReactNode; disabled?: boolean }) {
  return (
    <button
      type="button"
      aria-pressed={on}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        "min-h-[32px] rounded-full border px-3 text-[13px] leading-4 transition-colors",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400",
        on ? "border-indigo-300 bg-indigo-50 font-medium text-indigo-800" : "border-gray-200 bg-white text-gray-600 hover:border-gray-300",
        "disabled:cursor-not-allowed disabled:opacity-60",
      )}
    >
      {children}
    </button>
  );
}

/** A checkbox row with a 16px box and a 13px label, an optional hint under it. */
export function CheckRow({
  checked, onChange, label, hint, disabled = false,
}: { checked: boolean; onChange: (next: boolean) => void; label: string; hint?: string; disabled?: boolean }) {
  return (
    <label className={cn("flex items-start gap-2.5 py-1", disabled ? "cursor-not-allowed" : "cursor-pointer")}>
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-[1px] h-[18px] w-[18px] shrink-0 rounded border-gray-300 text-indigo-600 focus:ring-indigo-400 disabled:opacity-50"
      />
      <span className="min-w-0">
        <span className={cn("block text-[13px] leading-[20px]", disabled ? "text-gray-400" : "text-gray-800")}>{label}</span>
        {hint && <span className="block text-[12px] leading-4 text-gray-500">{hint}</span>}
      </span>
    </label>
  );
}

/** Why the field next to it did not save, announced as it appears. */
export function FieldError({ message }: { message: string | null | undefined }) {
  if (!message) return null;
  return (
    <p role="alert" className="mt-1 text-[12px] leading-4 text-red-700">
      {message}
    </p>
  );
}

/** A new item's or category's first step: its name, which creates it when it loses focus. */
export function NameStep({ busy, error, hint, onName }: { busy: boolean; error: string | null; hint: string; onName: (name: string) => void }) {
  return (
    <div className="space-y-2">
      <BlurText
        value=""
        onCommit={onName}
        required
        wrap
        autoFocus
        disabled={busy}
        ariaLabel="Nome"
        placeholder="Nome"
        className="-mt-4 rounded-none border-0 border-b border-gray-200 px-0 py-1 text-[18px] font-semibold leading-[25px] focus:border-indigo-300 focus:ring-0"
      />
      <FieldError message={error} />
      <p className="text-[12px] leading-4 text-gray-500">{hint}</p>
    </div>
  );
}
